import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listAuditEntries, exportAuditCsv } from '@/api/audit';
import Button from '@/components/ui/Button';
import Table, { type Column } from '@/components/ui/Table';
import Select from '@/components/ui/Select';
import IconButton from '@/components/ui/IconButton';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { AuditEntry } from '@/types';
import { formatPeriod, generateRecentPeriods } from '@/utils/format';

function actionBadge(action: string) {
  const colors: Record<string, string> = {
    CREATED: 'text-green-700',
    UPDATED: 'text-blue-700',
    DELETED: 'text-red-700',
    SUBMITTED: 'text-amber-700',
    AGREED: 'text-green-800',
    REOPENED: 'text-orange-700',
    STATUS_CHANGED: 'text-gray-700',
  };
  return (
    <span className={`text-xs font-semibold ${colors[action] ?? 'text-gray-600'}`}>
      {action}
    </span>
  );
}

const PAGE_SIZE = 50;

export default function AuditPage() {
  const recentPeriods = generateRecentPeriods(12);
  const [period, setPeriod] = useState(recentPeriods[0]);
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['audit', period, page],
    queryFn: () => listAuditEntries({ period, page, pageSize: PAGE_SIZE }),
    placeholderData: (prev) => prev,
  });

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportAuditCsv(period);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-${period}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const columns: Column<AuditEntry>[] = [
    {
      key: 'createdAt',
      header: 'When',
      sortable: true,
      render: (e) => (
        <span className="text-xs text-gray-500 whitespace-nowrap">
          {new Date(e.createdAt).toLocaleString('en-GB')}
        </span>
      ),
    },
    {
      key: 'actor',
      header: 'Who',
      render: (e) => (
        <span className="text-sm text-gray-800">
          {e.actor?.name ?? e.actor?.email ?? e.actorId}
        </span>
      ),
    },
    {
      key: 'entity',
      header: 'Entity',
      render: (e) => (
        <span className="text-sm text-gray-600">
          {e.entityCashFlow?.entity?.name ?? e.entityCashFlowId}
        </span>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (e) => actionBadge(e.action),
    },
    {
      key: 'field',
      header: 'Field',
      render: (e) => (
        <span className="text-xs text-gray-500">{e.fieldChanged ?? '—'}</span>
      ),
    },
    {
      key: 'before',
      header: 'Before',
      render: (e) => (
        <span className="font-mono text-xs text-red-700">
          {e.before != null ? JSON.stringify(e.before) : '—'}
        </span>
      ),
    },
    {
      key: 'after',
      header: 'After',
      render: (e) => (
        <span className="font-mono text-xs text-green-700">
          {e.after != null ? JSON.stringify(e.after) : '—'}
        </span>
      ),
    },
  ];

  const total = (data as { total?: number } | undefined)?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Trail</h1>
          <p className="mt-1 text-sm text-gray-500">
            Append-only log of all classification changes.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Select
            value={period}
            onChange={(e) => { setPeriod(e.target.value); setPage(1); }}
            options={recentPeriods.map((p) => ({ value: p, label: formatPeriod(p) }))}
            aria-label="Select period"
          />
          <Button variant="secondary" size="sm" loading={exporting} onClick={handleExport}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Export CSV
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        data={(data as { data?: AuditEntry[] } | undefined)?.data ?? []}
        loading={isLoading}
        rowKey={(e) => e.id}
        emptyMessage={`No audit entries for ${formatPeriod(period)}.`}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>
            Page {page} of {totalPages} ({total} entries)
          </span>
          <div className="flex gap-1">
            <IconButton
              icon={ChevronLeft}
              label="Previous page"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            />
            <IconButton
              icon={ChevronRight}
              label="Next page"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
